import React, { useState, useEffect } from "react";
import {
  Button,
  Checkbox,
  Col,
  DatePicker,
  Flex,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Table,
  Typography,
  message,
  Tooltip,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import {
  useGetMarketplaceOrderQuery,
  useGetUsimOrderIccidInventoryQuery,
  useGetUsimInventoryByMobileQuery,
  usePostUsimOrdersMutation
} from "../../../../api/usim/orders/UsimOrdersApi";
import { useGetUsimSkuByTypeQuery, useGetUsimSkusQuery } from "../../../../api/usim/uploads/products/productSkuApi";
import { useGetProductTypesQuery } from "../../../../api/usim/uploads/products/productTypeApi";
import { MarketOrderItem } from "../../../../api/usim/orders/MarketOrderItem";
import { UsimInventoryItem } from "../../../../api/usim/inventory/inventoryItem";
import { Auth } from 'aws-amplify';
import { UsimOrderSubmitData } from "../../../../api/usim/orders/UsimOrderItem";
import { InfoCircleOutlined } from "@ant-design/icons";
import { generateOrderNumber, getInventoryStatus, getColumns, TableRowData } from "./orderUtilities";
import { USimStatus, toUSimStatus } from "../../shared/usimUtils";
import {
  currencySymbol,
  isValidSalesAmount,
  SALES_AMOUNT_MIN,
  SALES_AMOUNT_VALIDATION_MESSAGE,
  SALES_PAYMENT_METHODS,
  SalesCurrency,
  SalesModeOfPayment,
} from "../../../redemption/manual/salesPayment";

const { TextArea } = Input;
const { Title } = Typography;

interface RetailOrderFormData {
  currency?: SalesCurrency;
  modeOfPayment?: SalesModeOfPayment;
  amountPaid?: string | null;
  orderProcessedDate: Dayjs | null;
  orderNumber: string;
  quantity: number;
  activationDate: Dayjs | null;
  productType: string;
  productPlan: string;
  comment: string;
  isRecharge: boolean;
  instantRecharge: boolean;
}

const isEmptyPaymentValue = (value: unknown) => (
  value === undefined || value === null || value === ""
);

export default function RetailOrderPage() {
  const [form] = Form.useForm();
  const currency = Form.useWatch("currency", form) as SalesCurrency | undefined;
  const validatePaymentGroupField = (label: string, value: unknown) => {
    const hasPayment = ["modeOfPayment", "amountPaid"].some(
      field => !isEmptyPaymentValue(form.getFieldValue(field)),
    );
    return hasPayment && isEmptyPaymentValue(value)
      ? Promise.reject(`${label} is required when payment information is entered`)
      : Promise.resolve();
  };
  const [tableData, setTableData] = useState<TableRowData[]>([]);
  const [orderNumber, setOrderNumber] = useState<string>("");
  const [quantity, setQuantity] = useState<number>(0);
  const [currentIccid, setCurrentIccid] = useState<string>("");
  const [currentMobileNumber, setCurrentMobileNumber] = useState<string>("");
  const [tempOrderNumber, setTempOrderNumber] = useState<string>("");
  const [tempIccid, setTempIccid] = useState<string>("");
  const [tempMobileNumber, setTempMobileNumber] = useState<string>("");
  const [tempRowKey, setTempRowKey] = useState<string>("");
  const [postUsimOrders] = usePostUsimOrdersMutation();
  const [marketOrder, setMarketOrder] = useState<MarketOrderItem>({} as MarketOrderItem);
  const [isSkuPresent, setIsSkuPresent] = useState<string>("");

  // auto recharge variables
  const [isRechargePlan, setIsRechargePlan] = useState<boolean>(false);
  const [isInstantRecharge, setIsInstantRecharge] = useState<boolean>(false);
  const [formData, setFormData] = useState<RetailOrderFormData>({
    currency: "PHP",
    orderProcessedDate: null,
    orderNumber: "",
    quantity: 0,
    activationDate: null,
    productType: "",
    productPlan: "",
    comment: "",
    isRecharge: false,
    instantRecharge: false,
  });

  // RTK Query hook for fetching order by ID
  const { data: orderData, error: orderError, isLoading } = useGetMarketplaceOrderQuery(
    orderNumber,
    { skip: !orderNumber, refetchOnMountOrArgChange: true } // Skip the query if orderNumber is empty

  );
  // RTK Query hook for fetching ICCID inventory details
  const { data: iccidInventory, error: iccidInventoryError } = useGetUsimOrderIccidInventoryQuery(
    currentIccid,
    { skip: !currentIccid } // Skip the query if no ICCID is being looked up
  );
  // RTK Query hook for fetching mobile inventory details
  const { data: mobileInventory, error: mobileInventoryError} = useGetUsimInventoryByMobileQuery(
    currentMobileNumber,
    { skip: !currentMobileNumber } // Skip the query if no mobile number is being looked up
  );

  // once selected it triggers type api to load the plans for that type.
  const [selectedProductType, setSelectedProductType] = useState<string>("");
  // load the plans
  const {
    data: productPlansForType,
    isFetching: productPlansForTypeFetching
  } = useGetUsimSkuByTypeQuery(
    selectedProductType,
    { skip: !selectedProductType }
  );
  const { data: productTypes } = useGetProductTypesQuery();


  // Transform product types for dropdown options
  const productTypesOptions = React.useMemo(() => {
    if (!productTypes) return [];
    //do not show item if status is INACTIVE
    console.log("product types", productTypes)
    return productTypes.filter(item => item.status !== false).map(item => ({
      label: item.name,
      value: item.name,
      iccid: item.iccid,
      id: item.id,
      hasActivation: item.activationDate ?? false
    }));
  }, [productTypes]);

  // Transform product plans for dropdown options
  const productPlansOptions = React.useMemo(() => {
    if (!productPlansForType) return [];
    //do not show item if status is INACTIVE
    return productPlansForType.filter(item => item.status).map(item => ({
      label: item.name,
      value: item.name,
      type: item.typeName
    }));
  }, [productPlansForType]);

  useEffect(() => {
    if (isSkuPresent != "") {
      message.warning('SKU is Not Available in System: ' + isSkuPresent);
    }
  }, [isSkuPresent]);

  function getProductType(orderData: any, skuName: string) {
    console.log("order data", orderData)
    if (orderData.sku_details) {
      const skuDetail = orderData.sku_details[skuName] ?? {};
      console.log("skuDetails", skuDetail)
      if (skuDetail.typeName) {
        return skuDetail.typeName;
      }
    }
    return "";
  }
  // Handle API response for orders
  useEffect(() => {
    form.setFieldsValue({
      orderProcessedDate: dayjs()
    });
    if (orderData) {
      // Extract data from the order
      let totalQuantity = 0;
      var skuItems: { sku: string; quantity: number; type?: string }[] = [];

      // Check if OrderDetails and item_list exist
      if (orderData.OrderDetails && orderData.OrderDetails.item_list) {
        // Loop through all items and accumulate quantities
        console.log("product plans options", productPlansOptions)
        orderData.OrderDetails.item_list.forEach((skuItem: any) => {
          if (skuItem.model_quantity_purchased && skuItem.model_sku) {
            if (skuItem.model_sku.toLowerCase().includes("-esim-")) {
              return;
            }
            const qty = Number(skuItem.model_quantity_purchased);
            totalQuantity += qty;
            const type = getProductType(orderData, skuItem.model_sku) || "";
            // const type = productPlansOptions.find(x => x.value === skuItem.model_sku)?.type || "";
            skuItems.push({
              sku: skuItem.model_sku,
              quantity: qty,
              type: type
            });
          }
        });
      }
      else{
        skuItems = []
      }

      setMarketOrder({ ...orderData, ShopId: String(orderData.ShopId) });
      form.setFieldsValue({
        orderNumber: orderData.OrderId || "",
        quantity: totalQuantity || 0,
        productPlan: skuItems[0]?.sku || "",
        productType: skuItems[0]?.type || "",
      });

      // Update form data state
      setFormData(prev => ({
        ...prev,
        orderNumber: orderData.OrderId || "",
        quantity: totalQuantity || 0,
        productPlan: skuItems[0]?.sku || "",
        productType: skuItems[0]?.type || "",
      }));

      // Update table data with correct quantities for each SKU
      let rowIndex = 0;
      const newTableData: TableRowData[] = [];

      // Create rows for each SKU and its quantity
      skuItems.forEach(item => {
        //  check if sku is present in system. 
        const missingSku = skuItems.find(item => !orderData.sku_details?.[item.sku]);
        setIsSkuPresent(missingSku?.sku ?? "");

        console.log("item", item.sku.startsWith("joy"))
        let is_joy = item.sku.startsWith("joy");
        const productTypeItem = productTypesOptions.find(opt => opt.value === item.type);
        const hasActivation = productTypeItem?.hasActivation ?? false;

        for (let i = 0; i < item.quantity; i++) {
          newTableData.push({
            key: `row-${rowIndex}`,
            iccid: "",
            mobileNumber: "",
            productType: item.type || "",
            productPlan: item.sku,
            status: is_joy ? USimStatus.PENDING_PROCESSING : USimStatus.PENDING_PROCESSING,
            activationDate: null,
            inventoryId: "",
            puk: "",
            hasActivation: hasActivation,
          });
          rowIndex++;
        }
      });

      setTableData(newTableData);

    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData]);

  // Show error message if API call fails
  useEffect(() => {
    if (orderError) {
      message.error('Failed to fetch order details');

    }
  }, [orderError]);

  // status definition
  // 1. if sku / type has arrival date required then "Pending-Activation"
  // 2. If sku / type has missing plan or have api recharge then "Pending-Recharge"
  // 3. if Inventory already have the sku then it's completed. 
  function validIccidToUse(inventoryItem:UsimInventoryItem){
    if (inventoryItem.ordernumber) {
      const isSameOrder = Array.isArray(inventoryItem.ordernumber) 
        ? inventoryItem.ordernumber.includes(orderNumber)
        : inventoryItem.ordernumber === orderNumber;

      if (isSameOrder || isRechargePlan) {
        return true;
      }

      Modal.confirm({
        title: "ICCID is Already Used in the order",
        content: <div>
          <p>Order Number: {Array.isArray(inventoryItem.ordernumber) ? inventoryItem.ordernumber.join(", ") : inventoryItem.ordernumber}</p>
          <p>ICCID: {inventoryItem.ICCID}</p>
        </div>
      })
      // clear the prefilled for this row only
      setTableData(prev => prev.map((row, index) => {
        if (row.key === tempRowKey) {
          return {
            ...row,
            mobileNumber: "",
            productType: "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: row.activationDate, // Preserve existing activation date
            inventoryId: "",
          };
        }
        return row;
      }));
      return false;
    }
    return true;
  }
  function setInventoryData(inventoryItem: UsimInventoryItem) {
    console.log("product plan", tableData )
    if(!validIccidToUse(inventoryItem)){
      return;
    }
    // Determine status based on ICCID data
    let status = getInventoryStatus(productTypesOptions,inventoryItem);
    console.log('status received', status)
    console.log("inventoryItem", inventoryItem.typeId) 
    
    setTableData(prev => prev.map((row, index) => {
      if (row.key === tempRowKey) {
        // For mobile number lookup, update ICCID without triggering the ICCID API
        const tempIccid = currentMobileNumber ? String(inventoryItem.ICCID || "") : row.iccid;
        const productTypeData = productTypesOptions?.find(x => x.id === inventoryItem.typeId);
        const localProductType = productTypeData?.label || "";
        const hasActivation = productTypeData?.hasActivation ?? row.hasActivation;
        console.log("row activation date", row.activationDate, row.productPlan)
        const baseUpdate = {
          ...row,
          iccid: tempIccid,
          mobileNumber: inventoryItem.mobileNumber || "",
          status: row.activationDate ?  "Pending-Activation": status,
          productType:  localProductType || "",
          productPlan: row.productPlan,
          inventoryId: inventoryItem.Id || "",
          activationDate: row.activationDate, // Preserve existing activation date
          hasActivation: hasActivation,
        };

        if (inventoryItem.planName !== "-") {
          baseUpdate.productPlan = inventoryItem.planName || "";
        }
        else {
          //load the plan names for dropdown for selected type
          // baseUpdate.productPlan = "";
          var tempTypeName = productTypes?.find(x => x.id === inventoryItem.typeId)?.name || "";
          setSelectedProductType(tempTypeName);


        }

        return baseUpdate;
      }
      return row;
    }));
  }

  function clearProductRow() {
    setTableData(prev => prev.map(row => {
      if (row.iccid === currentIccid) {
        return {
          ...row,
          mobileNumber: "",
          productType: "",
          status: USimStatus.PENDING_PROCESSING,
          activationDate: row.activationDate, // Preserve existing activation date
          inventoryId: "",
        };
      }
      return row;
    }));
    //  message.warning('No inventory found for this ICCID');
  }
  // Handle ICCID inventory data
  useEffect(() => {
    if (iccidInventory && iccidInventory.length > 0 && currentIccid) {
      const inventoryItem = iccidInventory.find(x => x.ICCID?.toString() == String(currentIccid));
      if (inventoryItem) {
        setInventoryData(inventoryItem);
      }
      else {
        setInventoryData(iccidInventory[0])
      }

    } else if (iccidInventory && iccidInventory.length === 0 && currentIccid) {
      // Clear row data if API returns empty result
      clearProductRow();

    }
  }, [iccidInventory, currentIccid]);

  useEffect(() => {
    if (mobileInventory && mobileInventory.length > 0 && currentMobileNumber) {
      const inventoryItem = mobileInventory[0];
      setInventoryData(inventoryItem);
    } else if (mobileInventory && mobileInventory.length === 0 && currentMobileNumber) {
      // Clear row data if API returns empty result
      clearProductRow();
      message.warning('No inventory found for this mobile number');
    }
  }, [mobileInventory, currentMobileNumber]);

  // Show error message if ICCID lookup fails
  useEffect(() => {
    if (iccidInventoryError) {
      // Clear row data if API returns error
      setCurrentIccid("");
      setTempIccid("");
      setTableData(prev => prev.map(row => {
        if (row.iccid === currentIccid) {
          return {
            ...row,
            mobileNumber: "",
            productType: "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: row.activationDate, // Preserve existing activation date
            inventoryId: "",
          };
        }
        return row;
      }));
      message.error('Failed to fetch ICCID details');
    }
  }, [iccidInventoryError, currentIccid]);

  // Show error message if mobile number lookup fails
  useEffect(() => {
    if (mobileInventoryError) {
      // Clear row data if API returns error

      setTableData(prev => prev.map(row => {
        if (row.mobileNumber === currentMobileNumber) {
          return {
            ...row,
            iccid: "",
            mobileNumber: "",
            productType: "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: row.activationDate, // Preserve existing activation date
            inventoryId: "",
          };
        }
        return row;
      }));
      message.error('Failed to fetch mobile number details');
    }
  }, [mobileInventoryError, currentMobileNumber]);

  const handleDeleteRow = (key: string) => {
    setTableData(prev => prev.filter(row => row.key !== key));
  };

  // Generate table rows based on quantity while preserving existing data
  useEffect(() => {
    setTableData(prevData => {
      const newRows: TableRowData[] = [];
      for (let i = 0; i < formData.quantity; i++) {
        // If row exists, preserve its data
        if (i < prevData.length) {
          const formattedFormDataActivationDate = formData.activationDate && typeof (formData.activationDate as any).format === 'function' ? (formData.activationDate as any).format('DD-MMM-YYYY') : formData.activationDate;
          const updatedActivationDate = prevData[i].activationDate || formattedFormDataActivationDate;
          let updatedStatus = prevData[i].status;

          if (formattedFormDataActivationDate && !prevData[i].activationDate) {
            updatedStatus = USimStatus.PENDING_PROCESSING;
          }

          const newProductType = prevData[i].productType || formData.productType || "";
          const productTypeItem = productTypesOptions.find(opt => opt.value === newProductType);
          const hasActivation = prevData[i].productType ? prevData[i].hasActivation : (productTypeItem?.hasActivation ?? false);
          console.log("productTypeItem in new", productTypeItem)


          newRows.push({
            ...prevData[i],
            // Only update these fields if they're empty
            productType: newProductType,
            productPlan: prevData[i].productPlan || formData.productPlan || "",
            activationDate: updatedActivationDate,
            status: updatedStatus,
            hasActivation: hasActivation
          });
        } else {
          const productTypeItem = productTypesOptions.find(opt => opt.value === formData.productType);
          console.log("productTypeItem", productTypeItem)
          const hasActivation = productTypeItem?.hasActivation ?? false;

          // Add new row with default values
          newRows.push({
            key: `row-${i}`,
            iccid: "",
            mobileNumber: "",
            productType: formData.productType || "",
            productPlan: formData.productPlan || "",
            status: USimStatus.PENDING_PROCESSING,
            activationDate: formData.activationDate,
            inventoryId: "",
            puk: "",
            hasActivation: hasActivation,
          });
        }
      }
      return newRows;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.quantity, formData.productType, formData.productPlan, formData.activationDate, productTypesOptions]);

  const handleFormValuesChange = (changedValues: any, allValues: any) => {
    // If order number changed, update temp state only
    if (changedValues.orderNumber !== undefined) {
      setTempOrderNumber(changedValues.orderNumber);
      return;
    }
    setFormData(allValues);
  };

  // Handle order number input blur
  const handleOrderNumberBlur = () => {
    if (tempOrderNumber !== orderNumber) {
      setOrderNumber(tempOrderNumber);
    }
  };

  const handleSubmit = async (values: RetailOrderFormData) => {
    if (!values.orderNumber) {
      message.error('Order number is required');
      return;
    }

    if (!values.quantity || values.quantity <= 0) {
      message.error('Quantity must be greater than 0');
      return;
    }

    // Check if all required ICCIDs are filled
    // const filledRows = tableData.filter(row => row.iccid.trim() !== '');
    // if (filledRows.length !== values.quantity) {
    //   message.error(`Please fill in all ${values.quantity} ICCID(s)`);
    //   return;
    // }

    const now = new Date().toISOString();
    let order_status: string = USimStatus.PENDING_PROCESSING;
    if (values.activationDate) {
      order_status = USimStatus.PENDING_PROCESSING;
    }
    tableData.map(row => {
      order_status = toUSimStatus(row.status);
    })
    let session = await Auth.currentUserInfo();
    const uname = session.username || "";
    // console.log("data in type", productTypes[0])
   
    const submitData: UsimOrderSubmitData = {
      OrderNumber: values.orderNumber,
      orderProcessedDate: values.orderProcessedDate?.toISOString() || now,
      type: values.productType,
      description: values.comment || '',
      sku: values.productPlan,
      quantity: values.quantity,
      status: order_status,
      processedBy: uname || "",
      productDetails: tableData.map(row => ({
        iccid: row.iccid,
        mobileNumber: row.mobileNumber,
        productType: row.productType,
        sku: row.productPlan,
        status: toUSimStatus(row.status),
        activationDate: row.activationDate ? dayjs(row.activationDate).format('YYYY-MM-DD') : undefined,
        inventoryId: row.inventoryId,
        puk: row.puk,
        shopId: marketOrder.ShopId,
        hasActivation: row.hasActivation,
      })),
      isRecharge: isRechargePlan,
      instantRecharge: isInstantRecharge,
      orderData: marketOrder,
    };

    if (!isEmptyPaymentValue(values.modeOfPayment) || !isEmptyPaymentValue(values.amountPaid)) {
      submitData.currency = values.currency;
      submitData.modeOfPayment = values.modeOfPayment;
      submitData.amountPaid = String(values.amountPaid);
    }

    if (submitData.productDetails.length == submitData.quantity && submitData.quantity > 0) {
      for (const productDetail of submitData.productDetails) {
        if (!productDetail.productType || productDetail.productType == "") {
          message.error('Product type is required for each product detail');
          return;
        }
      }
    }

    try {
      console.log("submit data", submitData)
      const response = await postUsimOrders(submitData).unwrap();
      message.success('Order submitted successfully');
      handleReset();

    } catch (error: any) {
      console.error('Error submitting order:', error);
      message.error(error.data.message || 'Failed to submit order');
    }
  };

  const onClickSubmit = async () => {
    try {
      const values = await form.validateFields();
      await handleSubmit(values);

      // Refresh page after 100ms to show clean state
      // setTimeout(() => window.location.reload(), 250);
    } catch (error) {
      if (error instanceof Error) {
        message.error(error.message);
      } else {
        message.error('Validation failed');
      }
    }
  };

  const handleReset = () => {
    console.log("function called")
    form.resetFields();
    setTempIccid("");
    setCurrentIccid("");
    setTempMobileNumber("")
    setCurrentMobileNumber("")
    setIsSkuPresent("");
    setMarketOrder({} as MarketOrderItem);
    setOrderNumber("");
    setIsRechargePlan(false);
    setIsInstantRecharge(false);
    setTempOrderNumber("");

    
     setFormData({
    currency: "PHP",
    orderProcessedDate: null,
    orderNumber: "",
    quantity: 0,
    activationDate: null,
    productType: "",
    productPlan: "",
    comment: "",
    isRecharge: false,
    instantRecharge: false,
  });
    // Explicitly set default values after reset
    form.setFieldsValue({
      currency: "PHP",
      modeOfPayment: undefined,
      amountPaid: undefined,
      orderProcessedDate: dayjs(),
      orderNumber: "",
      quantity: 0,
      activationDate: null,
      productType: "",
      productPlan: "",
      comment: "",
      planRecharge: false,
      instantRecharge: false
    });

    setTableData([]);

  };

  const handleTableDataChange = (
    key: string,
    field: keyof TableRowData,
    value: any
  ) => {
    setTableData((prev) =>
      prev.map((row) => (row.key === key ? { ...row, [field]: value } : row))
    );
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    currentKey: string,
    field: keyof TableRowData
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      console.log("enter pressed")
      // Find current row index
      const currentIndex = tableData.findIndex((row) => row.key === currentKey);
      if (currentIndex === -1) return;

      // Find next row
      const nextRow = tableData[currentIndex + 1];
      if (nextRow) {
        console.log("next row", nextRow);

        // Focus on the same field in the next row
        const nextInput = document.querySelector(
          `[data-row-key="${nextRow.key}"][data-field="${field}"]`
        ) as HTMLElement;
        if (nextInput) {
          console.log("next input", nextInput);
          nextInput.focus();
        }
      }
    }
  };

  const handleSelectKeyPress = (
    e: React.KeyboardEvent,
    currentKey: string,
    field: keyof TableRowData
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();

      // Find current row index
      const currentIndex = tableData.findIndex((row) => row.key === currentKey);
      if (currentIndex === -1) return;

      // Find next row
      const nextRow = tableData[currentIndex + 1];
      if (nextRow) {
        // Focus on the same field in the next row
        const nextInput = document.querySelector(
          `[data-row-key="${nextRow.key}"][data-field="${field}"]`
        ) as HTMLElement;
        if (nextInput) {
          nextInput.focus();
        }
      }
    }
  };

  const columns: ColumnsType<TableRowData> = getColumns({
    handleDeleteRow,
    handleTableDataChange,
    setTempIccid,
    setTempRowKey,
    handleKeyPress,
    tempIccid,
    currentIccid,
    setCurrentIccid,
    tempMobileNumber,
    currentMobileNumber,
    setCurrentMobileNumber,
    setTempMobileNumber,
    productTypesOptions,
    setSelectedProductType,
    handleSelectKeyPress,
    productPlansOptions,
    productPlansForTypeFetching
  });

 

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>Create New Retail Order</Title>
      <Form
        form={form}
        layout="horizontal"
        labelCol={{ span: 8 }}
        wrapperCol={{ span: 16 }}
        onValuesChange={handleFormValuesChange}
        initialValues={formData}
        onFinish={handleSubmit}
        style={{ maxWidth: "85%" }}
      >
        <Row gutter={24}>
          <Col span={12}>
            <Form.Item label="Order Processed Date" name="orderProcessedDate" getValueProps={(i) => ({ value: dayjs(i) })}>
              <DatePicker style={{ width: "80%" }} placeholder="Select date" format="DD-MMM-YYYY" />
            </Form.Item>

            <Row gutter={8}>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      <Tooltip title="Select if this order is a top-up (second recharge)." style={{ cursor: 'pointer', color: '#111111', backgroundColor: '#F2F2F2' }}>
                        <InfoCircleOutlined style={{ marginRight: 8 }} />
                      </Tooltip>
                      Recharge Plan
                    </span>
                  }
                  name="planRecharge"
                  labelCol={{ span: 16 }}
                  wrapperCol={{ span: 8 }}
                >
                  <Checkbox checked={isRechargePlan} onChange={(e) => {
                    const checked = e.target.checked;
                    setIsRechargePlan(checked);
                    if (checked) {
                      const newOrderNumber = generateOrderNumber();
                      setTempOrderNumber(newOrderNumber);
                      form.setFieldsValue({ orderNumber: newOrderNumber });
                    } else {
                      setTempOrderNumber("");
                      setOrderNumber("");
                      form.setFieldsValue({ orderNumber: "" });
                    }
                  }} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={
                    <span>
                      <Tooltip title="Select to load the plan immediately (for Joy blank SIMs only).">
                        <InfoCircleOutlined style={{ marginRight: 8 }} />
                      </Tooltip>
                      Instant Recharge
                    </span>
                  }
                  name="instantRecharge"
                  labelCol={{ span: 16 }}
                  wrapperCol={{ span: 8 }}
                >
                  <Checkbox checked={isInstantRecharge} onChange={(e) => setIsInstantRecharge(e.target.checked)} />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item label="Order Number" name="orderNumber" rules={[{ required: true, message: "Order Number is required" }]}>
              <Input
                style={{ width: "80%" }}
                placeholder="Enter order number"
                onChange={(e) => {
                  const value = e.target.value.trim();
                  setTempOrderNumber(value);
                }}
                value={orderNumber}
                onBlur={handleOrderNumberBlur}
              />
            </Form.Item>

            <Form.Item
              label="Quantity"
              name="quantity"
              rules={[{ required: true, message: "Quantity is required" }]}
            >
              <InputNumber
                min={0}
                style={{ width: "80%" }}
                max={150}
                placeholder="Enter quantity"
                onChange={(value) => {
                  setQuantity(value ?? 0);
                }}
              />
            </Form.Item>

            <Form.Item label="Activation Date" name="activationDate">
              <DatePicker
                style={{ width: "80%" }}
                placeholder="Select activation date"
                format="DD-MMM-YYYY"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Product Type" name="productType">
              <Select
                options={productTypesOptions}
                placeholder="Select product Type"
                showSearch
                style={{ width: "80%" }}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item label="Product SKU" name="productPlan" className="w-full">
              <Select
                options={productPlansOptions}
                placeholder="Select product SKU"
                style={{ width: "80%" }}
                showSearch
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item label="Comment" name="comment">
              <TextArea rows={3} placeholder="Enter comments" style={{ width: "80%" }} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={24}>
          <Col xs={24} md={8}>
            <Form.Item
              label="Currency"
              name="currency"
              dependencies={["modeOfPayment", "amountPaid"]}
              rules={[{ validator: (_, value) => validatePaymentGroupField("Currency", value) }]}
            >
              <Select
                allowClear
                options={[{ label: "PHP", value: "PHP" }, { label: "SGD", value: "SGD" }]}
                onChange={() => form.setFieldValue("modeOfPayment", undefined)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Mode of Payment"
              name="modeOfPayment"
              dependencies={["currency", "amountPaid"]}
              rules={[{ validator: (_, value) => validatePaymentGroupField("Mode of Payment", value) }]}
            >
              <Select
                allowClear
                disabled={!currency}
                placeholder="Select payment method"
                options={(currency ? SALES_PAYMENT_METHODS[currency] : []).map(method => ({
                  label: method,
                  value: method,
                }))}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item
              label="Amount Paid"
              name="amountPaid"
              dependencies={["currency", "modeOfPayment"]}
              rules={[{
                validator: async (_, value) => {
                  await validatePaymentGroupField("Amount Paid", value);
                  if (!isEmptyPaymentValue(value) && !isValidSalesAmount(value)) {
                    throw new Error(SALES_AMOUNT_VALIDATION_MESSAGE);
                  }
                },
              }]}
            >
              <InputNumber
                stringMode
                min={SALES_AMOUNT_MIN}
                precision={2}
                addonBefore={currency ? currencySymbol(currency) : undefined}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      {/* Table Section */}
      <Title level={3}>Product Details</Title>
      <Table
        columns={columns}
        dataSource={tableData}
        pagination={false}
        bordered
        size="small"
        scroll={{ x: "max-content" }}
        locale={{
          emptyText: "Enter quantity above to add product details",
        }}
      />
      {/* Action Buttons */}
      <Flex gap={24} justify="flex-end" style={{ marginTop: 16 }}>
        <Button onClick={handleReset}>Reset</Button>
        <Button type="primary" onClick={onClickSubmit} style={{ width: "120px" }}>
          Submit
        </Button>
      </Flex>
    </div>
  );
}
